import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { Icon, InputGroup } from '@blueprintjs/core';
import { css } from '@emotion/react';
import { Disclosure } from '@headlessui/react';
import { useCallback, useMemo, useState } from 'react';
import { ValueRenderers } from '../index';
import { Table } from '../table/Table';
const style = {
    container: css({
        padding: '5px',
    }),
    chevron: (open) => css({
        rotate: open ? '90deg' : '0deg',
        transition: 'all 0.3s ease-in-out',
    }),
    button: css({
        borderBottom: '1px solid #f5f5f5',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 2px',
        width: '100%',
        ':hover': {
            backgroundColor: '#f5f5f5',
        },
    }),
};
export function InfoPanel(props) {
    const [search, setSearch] = useState('');
    const { title = 'Information', data = [], titleStyle, inputStyle } = props;
    const viewData = useCallback((data) => {
        const exactMatch = [];
        const startsWith = [];
        const includes = [];
        const valueContains = [];
        for (const [key, value] of Object.entries(data).sort(([a], [b]) => a.localeCompare(b))) {
            const lowerKey = key.toLowerCase();
            const lowerSearch = search.toLowerCase();
            if (lowerKey === lowerSearch) {
                exactMatch.push([key, value]);
                continue;
            }
            if (lowerKey.startsWith(lowerSearch)) {
                startsWith.push([key, value]);
                continue;
            }
            if (lowerKey.includes(lowerSearch)) {
                includes.push([key, value]);
                continue;
            }
            if (valueSearch(value, search)) {
                valueContains.push([key, value]);
                continue;
            }
        }
        return [...exactMatch, ...startsWith, ...includes, ...valueContains];
    }, [search]);
    const { filteredData, total, count } = useMemo(() => {
        const filteredData = [];
        let total = 0;
        let count = 0;
        for (const { data: dataContent, ...other } of data) {
            total += Object.entries(dataContent).length;
            const content = viewData(dataContent);
            if (content.length === 0)
                continue;
            filteredData.push({ data: content, ...other });
            count += content.length;
        }
        return { filteredData, total, count };
    }, [data, viewData]);
    return (_jsxs("div", { css: style.container, children: [_jsx("div", { style: titleStyle, children: title }), _jsxs("div", { tabIndex: 0, css: css({
                    zIndex: 10,
                    marginTop: '5px',
                    position: 'sticky',
                    backgroundColor: 'white',
                    top: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                }), children: [_jsx(InputGroup, { css: css({
                            flexGrow: 1,
                        }), placeholder: "search for a parameter ...", value: search, onChange: ({ target }) => {
                            if (target.value !== undefined) {
                                setSearch(target.value);
                            }
                        }, style: inputStyle, leftIcon: "search", type: "search", fill: true }), "[", count, "/", total, "]"] }), _jsx("div", { style: {
                    marginTop: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                }, children: filteredData.map(({ description, data }) => {
                    return (_jsx(Disclosure, { defaultOpen: true, children: ({ open }) => (_jsxs(_Fragment, { children: [_jsxs(Disclosure.Button, { css: style.button, children: [_jsx(Icon, { icon: "chevron-right", css: style.chevron(open) }), description] }), _jsx(Disclosure.Panel, { children: _jsxs(Table, { striped: true, css: css({
                                            width: '100%',
                                        }), compact: true, children: [_jsxs(Table.Header, { children: [_jsx(ValueRenderers.Header, { value: "Parameter" }), _jsx(ValueRenderers.Header, { value: "Value" })] }), data.map(([key, value]) => (_jsxs(Table.Row, { style: {
                                                    height: '10px',
                                                    padding: '0 !imporant',
                                                }, children: [_jsx(ValueRenderers.Text, { value: key }), valueCell(value)] }, key)))] }) })] })) }, description));
                }) })] }));
}
/**
 * Get the value cell depending on the type of the value
 * @param value - ValueRenderers value.
 * @returns - ValueRenderers component.
 */
function valueCell(value) {
    switch (typeof value) {
        case 'boolean':
            return _jsx(ValueRenderers.Text, { value: value ? 'Yes' : 'No' });
        case 'number':
            return _jsx(ValueRenderers.Number, { value: value });
        case 'object':
            return _jsx(ValueRenderers.Object, { value: value });
        case 'string':
            return _jsx(ValueRenderers.Text, { value: value });
        default:
            _jsx(ValueRenderers.Text, { value: value });
    }
}
/**
 * Search a string in different type of values
 *
 * @param value - Value to search in.
 * @param search - Value to search for.
 * @returns - If search exist in value
 */
function valueSearch(value, search, lowerCase = true) {
    if (lowerCase) {
        value = String(value).toLowerCase();
        search = search.toLowerCase();
    }
    switch (typeof value) {
        case 'number':
            return String(value).includes(search);
        case 'boolean':
            return String(value).includes(search);
        case 'object':
            return JSON.stringify(value).includes(search);
        case 'string':
            return value.includes(search);
        default:
            return true;
    }
}
//# sourceMappingURL=InfoPanel.js.map